import { saveItem } from "action/shopping-cart/saveItem";
import { setProductDeliveryDetails } from "action/utils/setProductDeliveryDetails";
import { getProductDeliveryDetailsSkuApi } from "api/product/getProductDeliveryDetailsSkuApi";
import classNames from "classnames";
import { VEHICLE_FITMENT } from "constant/shared-constants";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { Heart } from "lucide-react";
import { useToast } from "modules/components/toast/ToastContext";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { isApparelProduct } from "util/product/apparel";
import { PRODUCT_PAGE_TYPES } from "util/product/product-constants";
import PAButton, { BUTTON_TYPE } from "../../../design-system/buttons/PAButton";
import ProductQuantity from "../../../design-system/product-quantitiy/ProductQuantity";
import styles from "./addToCartContainer.module.scss";
const AddtoCartButton = dynamic(() => import(/* webpackChunkName: "add-to-cart-button" */"component/design-system/buttons/AddtoCartButton"));

function AddToCartContainer(props: any) {
  const {
    outOfStock, limitedQuantityDetails,
    pickUpAvailable, shippingAvailable, saveButtonWithText = false, product,
    classActionContainer = "", handleSaveItemForLater,
    setSyncProduct, addingToCartLocation, showSaveButton = true,
    quantityClass, showLabel, showQuantity = true, showAddToCart = true, merchandiseSizes,
    addToCartButtonType, addToCartClassName, categoryPage = false,
  } = props;

  const dispatch = useDispatch();
  const { showToast } = useToast();

  const { savedItems, postalCode } = useSelector((store: RootState) => store.shoppingCart);
  const { selectedVehicle } = useSelector((store: RootState) => store.garage);
  const { syncedProduct, currentPageType } = useSelector((state: RootState) => state.utils);
  const isThisSavedItem = savedItems.some((item: any) => item.sku === product.sku);
  const isApparel = isApparelProduct(product);
  const [isMobile] = useScreenSize();
  const { t } = useTranslation("product");

  const buttonRef = useRef(null);
  const [isFixedButton, setIsFixedButton] = useState(false);
  const [productQuantity, setQuantityState] = useState(1);

  const { quantity } = syncedProduct || {};
  let buttonText = t("Save");
  if (isThisSavedItem) {
    buttonText = t("Saved!");
  }

  const renderSaveIcon = (className = "") => (
    <Heart
      size={25}
      className={className}
      fill={isThisSavedItem ? "#f44336" : "none"}
      strokeWidth={isThisSavedItem ? 0 : 2}
    />
  );

  useEffect(() => {
    let buttonTop = null;

    if (buttonTop === null && !isFixedButton) {
      buttonTop = buttonRef?.current?.getBoundingClientRect().top - window.innerHeight + window.scrollY + buttonRef?.current?.offsetHeight;
      setIsFixedButton(true);
    }

    const handleScroll = () => {
      if (!isMobile) {
        return;
      }
      if (window.scrollY < buttonTop) {
        setIsFixedButton(true);
      } else {
        setIsFixedButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const callbackAfterSave = () => {
    const message = isThisSavedItem ? "Item is removed from wishlist" : "Item is moved to wishlist";
    showToast({ type: "success", message, iconClass: "fa-check" });
  };

  const handleSaveItemForLaterHandler = () => {
    const saveOrRemove = !isThisSavedItem;
    dispatch(saveItem(
      product,
      selectedVehicle,
      callbackAfterSave,
      saveOrRemove ? (product.fitment === VEHICLE_FITMENT.UNIVERSAL || isApparel) : true,
      saveOrRemove ? 1 : 0,
    ));
  };

  const handleUpdateQuantity = (newQuantity: number) => {
    setQuantityState(newQuantity);
    if (PRODUCT_PAGE_TYPES.includes(currentPageType)) {
      getProductDeliveryDetailsSkuApi(product.sku, postalCode, newQuantity)
        .then(({ data }) => {
          const { productDeliveryDetails } = data;
          dispatch(setProductDeliveryDetails(productDeliveryDetails.availableDeliveryMethodsDetails));
        });
    }
  };

  return (
    <div className={`${styles.action_container} ${classActionContainer}`}>

      <div className="d-flex align-items-center justify-content-end">
        {/* TODO fix this button behaviour and test action */}
        <HideIf show={showSaveButton}>
          <HideIf show={saveButtonWithText}>
            <PAButton
              buttonType={BUTTON_TYPE.SECONDRY_OUTLINE}
              onClick={handleSaveItemForLater || handleSaveItemForLaterHandler}
              className={styles.save_button_with_text}
            >

              {renderSaveIcon()}

              <text>{` ${buttonText} `}</text>
            </PAButton>
          </HideIf>
          <HideIf show={!saveButtonWithText && !categoryPage}>
            <PAButton
              buttonType={BUTTON_TYPE.SECONDRY_OUTLINE}
              onClick={handleSaveItemForLater || handleSaveItemForLaterHandler}
              className={styles.save_button}
            >
              {renderSaveIcon(isThisSavedItem ? styles.wishlist_icon : "")}
              {/* <PAImage
                src={isThisSavedItem ? "/wishlist_saved.svg" : "/wishlist_not_saved.svg"}
                width={25}
                height={25}
                alt={isThisSavedItem ? "Remove this product from wishlist" : "Reserve this product for future shopping"}
                title={isThisSavedItem ? "Remove this product from wishlist" : "Reserve this product for future shopping"}
              /> */}
            </PAButton>
          </HideIf>
        </HideIf>
        <HideIf show={showAddToCart}>
          <AddtoCartButton
            quantity={setSyncProduct ? quantity : productQuantity}
            product={product}
            {...{
              pickUpAvailable,
              shippingAvailable,
            }}
            className={classNames(
              styles.add_to_cart_button,
              addToCartClassName,
              {
                [styles.static_add_cart_button]: !isFixedButton,
              },
            )}
            addingToCartLocation={addingToCartLocation}
            showLabel={showLabel}
            merchandiseSizes={merchandiseSizes}
            buttonRef={buttonRef}
            buttonType={addToCartButtonType}
            categoryPage={categoryPage}
          />
        </HideIf>

        <HideIf show={showQuantity}>
          <ProductQuantity
            {...{
              limitedQuantityDetails,
              outOfStock,
              sku: product.sku,
            }}
            className={quantityClass}
            productQuantity={setSyncProduct ? quantity : productQuantity}
            setQuantityState={handleUpdateQuantity}
            setSyncProduct={setSyncProduct}
          />
        </HideIf>

      </div>
    </div>
  );
}

export default AddToCartContainer;
